import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle, AlertCircle, Info, Pencil } from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, subMonths, addMonths } from 'date-fns';

const DateTimePicker = ({
  onSelect,
  selectedDate,
  selectedTime,
  availabilityData,
  onDateChange,
  loading = false,
  maxDaysAhead = 30
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // The slot list is 96 entries tall. Once a time is picked we collapse it to a single chip
  // so the rest of the form is reachable without scrolling past the whole day.
  const [timeListOpen, setTimeListOpen] = useState(false);

  // Picking a date (or clearing the time) should always land on an open list.
  useEffect(() => {
    if (selectedDate && !selectedTime) setTimeListOpen(true);
  }, [selectedDate, selectedTime]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = monthStart.getDay();
  const maxDate = addDays(new Date(), maxDaysAhead);

  const handlePrevMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    if (newMonth >= startOfMonth(new Date())) {
      setCurrentMonth(newMonth);
    }
  };

  const handleNextMonth = () => {
    const newMonth = addMonths(currentMonth, 1);
    if (newMonth <= addMonths(new Date(), 2)) {
      setCurrentMonth(newMonth);
    }
  };

  const handleDateSelect = (date) => {
    // Tapping the already-selected date reopens the slot list — the second way back to it,
    // alongside the chip itself.
    if (selectedDate && isSameDay(date, selectedDate)) {
      setTimeListOpen((open) => !open);
      return;
    }
    onDateChange(date);
    setTimeListOpen(true);
  };

  const handleTimeSelect = (time) => {
    onSelect(selectedDate, time);
    setTimeListOpen(false);
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    return dateToCheck < today || dateToCheck > maxDate;
  };

  const getTimeSlotStyle = (slot) => {
    if (!slot.available) {
      return 'bg-red-50/80 text-red-400 cursor-not-allowed border-red-200';
    }
    if (slot.status === 'limited') {
      return 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-400 cursor-pointer';
    }
    return 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-400 cursor-pointer';
  };

  // Group slots by hour for cleaner display (4 slots per hour: 00, 15, 30, 45)
  const hourlyGroups = availabilityData?.timeSlots?.length > 0
    ? (() => {
        const acc = {};
        availabilityData.timeSlots.forEach((slot) => {
          const hour = slot.time.split(':')[0];
          if (!acc[hour]) acc[hour] = [];
          acc[hour].push(slot);
        });
        // Sort by hour (00, 01, ..., 23)
        return Object.keys(acc)
          .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
          .reduce((obj, key) => {
            obj[key] = acc[key];
            return obj;
          }, {});
      })()
    : {};

  return (
    <div className="space-y-6">
      {/* 15-min gap info banner */}
      <div className="flex items-start gap-3 p-4 bg-railway-50 rounded-xl border border-railway-200">
        <Info className="w-5 h-5 text-railway-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-railway-800">
          <p className="font-medium">Book your preferred time</p>
          <p className="text-railway-600 mt-0.5">
            Slots are available every <strong>15 minutes</strong> throughout the day. 
            A 15-minute gap is maintained between bookings for room preparation.
          </p>
        </div>
      </div>

      {/* Date Selection */}
      <div className="bg-white rounded-2xl border border-midnight-100 overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-railway-500 to-railway-600 text-white p-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="font-semibold text-lg">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2 text-sm text-white/80">
            <Calendar className="w-4 h-4" />
            <span>Select your check-in date</span>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-midnight-100">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="py-3 text-center text-sm font-medium text-midnight-500 bg-midnight-50">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 p-2 gap-1">
          {Array.from({ length: startDayOfWeek }).map((_, index) => (
            <div key={`empty-${index}`} className="h-12" />
          ))}

          {calendarDays.map((date) => {
            const isDisabled = isDateDisabled(date);
            const isSelected = selectedDate && isSameDay(date, selectedDate);
            const isTodayDate = isToday(date);

            return (
              <button
                key={date.toISOString()}
                type="button"
                onClick={() => !isDisabled && handleDateSelect(date)}
                disabled={isDisabled}
                className={`
                  h-12 rounded-xl flex flex-col items-center justify-center transition-all
                  ${isDisabled
                    ? 'text-midnight-300 cursor-not-allowed'
                    : 'hover:bg-railway-50 hover:border-railway-300 cursor-pointer'
                  }
                  ${isSelected
                    ? 'bg-railway-500 text-white shadow-lg shadow-railway-500/30 scale-105'
                    : ''
                  }
                  ${isTodayDate && !isSelected
                    ? 'border-2 border-railway-400 font-bold'
                    : 'border border-transparent'
                  }
                `}
              >
                <span className={`text-sm ${isSelected ? 'text-white' : ''}`}>
                  {format(date, 'd')}
                </span>
                {isTodayDate && !isSelected && (
                  <span className="text-[10px] text-railway-500 font-medium">Today</span>
                )}
              </button>
            );
          })}
        </div>

        {selectedDate && (
          <div className="border-t border-midnight-100 p-4 bg-railway-50/50">
            <div className="flex items-center gap-2 text-railway-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">
                Selected: {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Time Selection - 15-min slots */}
      {selectedDate && (
        <div className="bg-white rounded-2xl border border-midnight-100 overflow-hidden shadow-sm animate-slide-up">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <h3 className="font-semibold">
                  {selectedTime && !timeListOpen ? 'Check-in Time' : 'Select Check-in Time'}
                </h3>
              </div>
              {selectedTime && (
                <button
                  type="button"
                  onClick={() => setTimeListOpen((open) => !open)}
                  className="px-2.5 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-xs font-semibold transition-colors"
                >
                  {timeListOpen ? 'Close' : 'Change'}
                </button>
              )}
            </div>
            <p className="text-sm text-white/80 mt-1">
              Slots every 15 min • {format(selectedDate, 'MMM d, yyyy')}
            </p>
          </div>

          {/* Collapsed: just the chosen slot. Click it (or the date again) to reopen. */}
          {selectedTime && !timeListOpen ? (
            <button
              type="button"
              onClick={() => setTimeListOpen(true)}
              className="w-full p-4 flex items-center gap-3 text-left hover:bg-blue-50/60 transition-colors group"
            >
              <span className="flex flex-col items-center justify-center w-20 h-16 rounded-xl bg-railway-500 text-white shadow-lg shadow-railway-500/30 flex-shrink-0">
                <span className="text-lg font-bold leading-none">{selectedTime}</span>
                <span className="text-[10px] opacity-80 mt-1">check-in</span>
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 font-semibold text-midnight-900">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {format(selectedDate, 'EEE, MMM d, yyyy')}
                </span>
                <span className="flex items-center gap-1 text-sm text-midnight-500 mt-0.5 group-hover:text-railway-600 transition-colors">
                  <Pencil className="w-3 h-3" />
                  Tap to pick a different time
                </span>
              </span>
            </button>
          ) : (
          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-railway-500 border-t-transparent"></div>
              </div>
            ) : Object.keys(hourlyGroups).length > 0 ? (
              <>
                <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="text-midnight-600">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span className="text-midnight-600">Limited</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <span className="text-midnight-600">Full</span>
                  </div>
                </div>

                <div className="max-h-[320px] overflow-y-auto pr-2 space-y-4">
                  {Object.entries(hourlyGroups).map(([hour, slots]) => (
                    <div key={hour} className="space-y-2">
                      <div className="text-xs font-semibold text-midnight-500 uppercase tracking-wider">
                        {parseInt(hour, 10) === 0 ? '12 AM' : parseInt(hour, 10) < 12 ? `${parseInt(hour, 10)} AM` : parseInt(hour, 10) === 12 ? '12 PM' : `${parseInt(hour, 10) - 12} PM`}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {slots.map((slot) => (
                          <button
                            key={slot.time}
                            type="button"
                            onClick={() => slot.available && handleTimeSelect(slot.time)}
                            disabled={!slot.available}
                            className={`
                              relative py-2.5 px-2 rounded-xl border-2 font-medium text-sm transition-all
                              ${getTimeSlotStyle(slot)}
                              ${selectedTime === slot.time ? 'ring-2 ring-railway-500 ring-offset-2' : ''}
                            `}
                          >
                            <div className="font-semibold">{slot.label}</div>
                            <div className="text-[10px] opacity-70 mt-0.5">
                              {slot.available ? `${slot.availableRooms} left` : 'Full'}
                            </div>
                            {selectedTime === slot.time && (
                              <div className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-railway-500 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <p className="text-midnight-600 font-medium">No time slots available</p>
                <p className="text-sm text-midnight-400 mt-1">Please select a different date.</p>
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {/* The collapsed time chip above already states the full date and time, so the old
          summary card was a third copy of the same thing — and more scrolling. */}
    </div>
  );
};

export default DateTimePicker;
